import { zodResolver } from "@hookform/resolvers/zod";
import { KdsModal } from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { type ContextType, useCallback, useMemo, useState } from "react";
import {
  FormProvider,
  type SubmitHandler,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import {
  FileContext,
  FileContextProvider,
} from "@/components/FileUploadAsync/FileUploadAsync.context";
import { useToasts } from "@/hooks";
import { useDirectUpload } from "@/hooks/useDirectUpload";
import { useOrderItemsPreMedia } from "@/oms/hooks/queries/useOrderItemPreMedia";
import {
  PreMediaBriefSection,
  PreMediaCommentSection,
  PreMediaFilesSection,
} from "@/pages/OrderItemPage/_partials";
import {
  type PreMediaClientRequestDetailsFormSchema,
  preMediaClientRequestDetailsFormSchema,
} from "./ModalPreMediaClientRequestDetailsForm.schema";
import { type ModalPreMediaClientRequestDetailsFormProps } from "./ModalPreMediaClientRequestDetailsForm.types";

export const ModalPreMediaClientRequestDetailsForm = ({
  orderItem,
  isOpen,
  setOpenState,
  csmComment,
}: ModalPreMediaClientRequestDetailsFormProps) => {
  const { t } = useTranslate();
  const { add } = useToasts();
  const { useAddPreMediaClientRequestDetails } = useOrderItemsPreMedia();
  const { mutateAsync: addPreMediaClientRequestDetails, isPending } =
    useAddPreMediaClientRequestDetails();

  const { uploadFile, createFileUploadUrl } = useDirectUpload();

  const methods = useForm<PreMediaClientRequestDetailsFormSchema>({
    resolver: zodResolver(preMediaClientRequestDetailsFormSchema),
    defaultValues: {
      preMediaFiles: orderItem.sandboxItem?.preMediaFiles
        ? [
            ...orderItem.sandboxItem.preMediaFiles.map((file) => ({
              blobId: file.blobId,
              fileName: file.fileName,
              size: file.size,
              url: file.url,
            })),
            { fileSignedId: "" },
          ]
        : [{ fileSignedId: "" }],
      preMediaBrief: orderItem.sandboxItem?.preMediaBrief || "",
    },
  });

  const [isUploadingFile, setIsUploadingFile] = useState<boolean>(false);

  const { fields } = useFieldArray({
    control: methods.control,
    name: "preMediaFiles",
    rules: { required: true },
  });

  const preMediaBrief = useWatch({
    control: methods.control,
    name: "preMediaBrief",
  });

  const isPreMediaBriefMissing = useMemo(() => !preMediaBrief, [preMediaBrief]);

  const handleSubmit = useCallback<
    SubmitHandler<PreMediaClientRequestDetailsFormSchema>
  >(
    async (data) => {
      if (!orderItem.sandboxItem) {
        return;
      }

      const res = await addPreMediaClientRequestDetails({
        orderId: orderItem.orderId,
        orderItemId: orderItem.id,
        command: {
          preMediaBrief: data.preMediaBrief,
          SandboxItemPreMediaFiles:
            data.preMediaFiles
              ?.filter((file) => Boolean(file.fileSignedId))
              .map((file) => ({
                fileSignedId: file.fileSignedId as string,
              })) || [],
        },
      });

      if (res.status === 200 || res.status === 201) {
        add({ state: "success", title: t("toast.type.success") });
        setOpenState(false);
        return;
      }

      add({ state: "error", title: t("toast.generic.error") });
    },
    [orderItem, addPreMediaClientRequestDetails, add, setOpenState, t]
  );

  const handleModalClose = useCallback(() => {
    if (isOpen) {
      methods.clearErrors();
    }
    setOpenState(!isOpen);
  }, [isOpen, methods, setOpenState]);

  const handleCancel = useCallback(() => {
    methods.clearErrors();
    setOpenState(false);
  }, [methods, setOpenState]);

  const handleFileUpload = useCallback(
    async (file: File, index: number, ctx: ContextType<typeof FileContext>) => {
      setIsUploadingFile(true);
      try {
        const uploadDetails = await createFileUploadUrl(
          "sandboxItem.preMedia",
          file
        );
        const newSignedId = uploadDetails?.fileSignedId;

        ctx?.pushFile(newSignedId, {
          fileName: file.name,
          size: file.size,
          contentType: file.type,
        });

        await uploadFile(file, uploadDetails);
        ctx?.setFileUploadState(newSignedId, "uploaded");
        methods.setValue(`preMediaFiles.${index}.fileSignedId`, newSignedId);
      } finally {
        setIsUploadingFile(false);
      }
    },
    [uploadFile, createFileUploadUrl, methods]
  );

  return (
    <FormProvider {...methods}>
      <FileContextProvider>
        <FileContext.Consumer>
          {(ctx) => (
            <KdsModal
              isOpen={isOpen}
              onOpenChange={handleModalClose}
              dialogProps={{
                dialogHeaderProps: {
                  title: t(
                    "order.myOrdersItemDetails.preMedia.addPreMediaRequestDetails"
                  ),
                  description: t(
                    "order.myOrdersItemDetails.preMedia.addPreMediaDetailsDescription"
                  ),
                },
                content: (
                  <div className="mx-4 flex flex-col gap-8">
                    <PreMediaCommentSection csmComment={csmComment} />
                    <PreMediaFilesSection
                      orderItem={orderItem}
                      fields={fields}
                      isUploadingFile={isUploadingFile}
                      onFileUpload={handleFileUpload}
                      ctx={ctx}
                    />
                    <PreMediaBriefSection
                      isPreMediaBriefMissing={isPreMediaBriefMissing}
                    />
                  </div>
                ),
                dialogFooterProps: {
                  buttonProps: [
                    {
                      children: t("buttons.cancel"),
                      variant: "ghost",
                      color: "neutral",
                      onPress: handleCancel,
                    },
                    {
                      children: t("buttons.send"),
                      type: "submit",
                      isLoading: isPending,
                      disabled: isPending || ctx?.isUploading,
                      onPress: () => void methods.handleSubmit(handleSubmit)(),
                    },
                  ],
                },
              }}
            />
          )}
        </FileContext.Consumer>
      </FileContextProvider>
    </FormProvider>
  );
};
